import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Copy, Eye, Link2, Loader2, PhoneCall, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/te-previews`;
const auth = () => {
  const t = localStorage.getItem("session_token");
  return { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} };
};
const day = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const EMPTY = { customer_name: "", customer_email: "", school_name: "", band_token: "", note: "", send_email: true };

const copy = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success("Link copied"); }
  catch { window.prompt("Copy this link:", text); }
};

const statusOf = (l) => (l.status !== "active" ? "Turned off" : l.expired ? "Expired" : "Active");

const LinkRow = ({ l, onToggle }) => (
  <tr className="border-t border-slate-100" data-testid={`te-link-row-${l.id}`}>
    <td className="p-2"><div className="font-medium text-slate-900">{l.customer_name}</div>
      <div className="text-slate-500">{l.customer_email}{l.school_name ? ` · ${l.school_name}` : ""}</div></td>
    <td className="p-2">{l.band_label}</td>
    <td className="p-2">{day(l.created_at)}<div className="text-slate-500">until {day(l.expires_at)}</div></td>
    <td className="p-2" data-testid={`te-link-opened-${l.id}`}>
      {l.first_opened_at ? <span className="text-emerald-700 font-medium">Opened {l.open_count}×</span> : <span className="text-slate-500">Not yet</span>}
      {l.last_opened_at && <div className="text-slate-500">last {day(l.last_opened_at)}</div>}
      {l.quote_number && <div className="text-emerald-700 font-medium" data-testid={`te-link-quote-${l.id}`}>Quote {l.quote_number}</div>}
    </td>
    <td className="p-2" data-testid={`te-link-status-${l.id}`}>{statusOf(l)}</td>
    <td className="p-2 whitespace-nowrap space-x-1">
      <Button size="sm" variant="outline" onClick={() => copy(l.link)} data-testid={`te-link-copy-${l.id}`}><Copy className="h-3 w-3" /></Button>
      <Button size="sm" variant="ghost" onClick={() => onToggle(l)} data-testid={`te-link-toggle-${l.id}`}>
        {l.status === "active" ? "Turn off" : "Turn on"}
      </Button>
    </td>
  </tr>
);

const LinkForm = ({ bands, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(BASE, { ...f, origin: window.location.origin }, auth());
      setMade(r.data); setF(EMPTY); onCreated();
      if (f.send_email) r.data.email_sent_at ? toast.success("Demo link emailed") : toast.error("Link made, but the email did not send. Use Copy.");
    } catch (err) { toast.error(err?.response?.data?.detail?.toString() || "Could not create link"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="grid gap-2 md:grid-cols-2" data-testid="te-link-form">
      <Input placeholder="Customer name" value={f.customer_name} onChange={set("customer_name")} required data-testid="te-link-name" />
      <Input type="email" placeholder="Customer email" value={f.customer_email} onChange={set("customer_email")} required data-testid="te-link-email" />
      <Input placeholder="School (optional)" value={f.school_name} onChange={set("school_name")} data-testid="te-link-school" />
      <select className="h-9 rounded-md border border-slate-200 px-2 text-sm" value={f.band_token} onChange={set("band_token")} required data-testid="te-link-band">
        <option value="">Pick a grade band…</option>
        {bands.map((b) => <option key={b.band_token} value={b.band_token}>{b.label}</option>)}
      </select>
      <Input className="md:col-span-2" placeholder="Short personal note in the email (optional)" value={f.note} onChange={set("note")} data-testid="te-link-note" />
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={f.send_email} onChange={set("send_email")} data-testid="te-link-send-email" /> Email the link to the customer for me
      </label>
      <Button type="submit" disabled={busy} data-testid="te-link-create-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Create 14-day demo link
      </Button>
      {made && (
        <div className="md:col-span-2 flex items-center gap-2 rounded bg-emerald-50 p-2 text-sm" data-testid="te-link-created">
          <Link2 className="h-4 w-4 text-emerald-700" />
          <span className="truncate flex-1" data-testid="te-link-created-url">{made.link}</span>
          <Button size="sm" type="button" variant="outline" onClick={() => copy(made.link)} data-testid="te-link-created-copy">Copy link</Button>
          <a href={made.link} target="_blank" rel="noreferrer" className="text-emerald-800 underline" data-testid="te-link-created-open">Open</a>
        </div>
      )}
    </form>
  );
};

const CallToday = ({ rows, onCalled }) => (
  <div className="rounded border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="te-call-today">
    <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
      <PhoneCall className="h-4 w-4" /> Call today: {rows.length} customer(s) opened their preview in the last 48 hours
    </div>
    {rows.map((l) => (
      <div key={l.id} className="flex items-center justify-between gap-3 rounded bg-white p-2 text-xs" data-testid={`te-call-row-${l.id}`}>
        <div>
          <span className="font-medium text-slate-900">{l.customer_name}</span>
          <span className="text-slate-500"> · {l.school_name || "—"} · {l.band_label} · opened {l.open_count}× · last {new Date(l.last_opened_at).toLocaleString()}</span>
          {l.quote_number && <span className="ml-2 font-semibold text-emerald-700">Asked for quote {l.quote_number}</span>}
          <div><a className="text-[#002FA7] underline" href={`mailto:${l.customer_email}`}>{l.customer_email}</a></div>
        </div>
        <Button size="sm" variant="outline" onClick={() => onCalled(l)} data-testid={`te-call-done-${l.id}`}>Mark called</Button>
      </div>
    ))}
  </div>
);

export const TePreviewLinksPanel = () => {
  const [bands, setBands] = useState([]);
  const [links, setLinks] = useState([]);
  const [calls, setCalls] = useState([]);

  const load = useCallback(async () => {
    try { setLinks((await axios.get(BASE, auth())).data.links); } catch { /* shown empty */ }
    try { setCalls((await axios.get(`${BASE}/call-today`, auth())).data.links); } catch { /* shown empty */ }
  }, []);
  useEffect(() => {
    axios.get(`${BASE}/bands`, auth()).then((r) => setBands(r.data.bands)).catch(() => {});
    load();
  }, [load]);

  const called = async (l) => {
    try { await axios.post(`${BASE}/${l.id}/called`, {}, auth()); toast.success("Marked as called"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.toString() || "Could not update"); }
  };

  const toggle = async (l) => {
    try {
      await axios.patch(`${BASE}/${l.id}`, { status: l.status === "active" ? "disabled" : "active" }, auth());
      toast.success(l.status === "active" ? "Link turned off" : "Link turned on");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail?.toString() || "Could not update link"); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4" data-testid="te-preview-links-panel">
      <div className="flex items-start gap-3">
        <Eye className="h-5 w-5 text-[#002FA7] mt-0.5" />
        <div>
          <h3 className="font-semibold text-slate-900">Send a Teacher Edition Preview</h3>
          <p className="text-sm text-slate-600">A private link that opens a sample Teacher Edition in the customer's browser, with a sample class of 15 and a guided tour. Works for 14 days. You get an email the first time they open it.</p>
        </div>
      </div>
      {calls.length > 0 && <CallToday rows={calls} onCalled={called} />}
      <LinkForm bands={bands} onCreated={load} />
      {links.length > 0 && (
        <div className="overflow-auto rounded border border-slate-100">
          <table className="w-full text-xs" data-testid="te-links-table">
            <thead className="bg-slate-50 text-left"><tr>
              <th className="p-2">Customer</th><th className="p-2">Grade band</th><th className="p-2">Sent</th>
              <th className="p-2">Opened</th><th className="p-2">Status</th><th className="p-2" />
            </tr></thead>
            <tbody>{links.map((l) => <LinkRow key={l.id} l={l} onToggle={toggle} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
